import React, { ChangeEvent, forwardRef, KeyboardEvent, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from '../AutoSuggest/AutoSuggest.module.scss';
import { Option, OptionGroup } from '@/types/Search';
import { AirplaneIcon, HotelPropertyIcon, MapPinLineIcon, PointIcon, SearchIcon } from '@/components/icons';
import { useTranslations } from 'next-intl';
import { setSearchValues } from '@/redux/reducer/initSearchSlice';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/types/RootState';
import { loadDestination } from '@/utils/search/destination';
import Loader from '@/layout/Loader/Loader';
import { Skeleton } from '@mui/material';


interface AutoSuggestMobileProps {
  name: string;
  value?: Option | null;
  errors?: any;
  touched?: any;
  label?: string;
  errorStyle: boolean;
  loadOptions: (
    inputValue: string,
    signal?: AbortSignal
  ) => Promise<{ options: OptionGroup[]; hasMore: boolean }>;
  className?: string;
  placeholder: string;
  onOptionSelect: (option: Option | null) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;

}

export interface AutoSuggestMobileRef {
  clearInput: () => void;
  focusAndSelect: () => void;
}

const AutoSuggestMobile = forwardRef<AutoSuggestMobileRef, AutoSuggestMobileProps>(({
  errorStyle,
  loadOptions,
  placeholder,
  onOptionSelect,
  onClick,

}, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [currentOptions, setCurrentOptions] = useState<OptionGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);
  const [hasFocused, setHasFocused] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [loadingLabel, setLoadingLabel] = useState<boolean>(false);

  const t = useTranslations();

  const memoizedLoadOptions = useCallback(
    (input: string, signal?: AbortSignal) => loadOptions(input, signal),
    [loadOptions]
  );



  useEffect(() => {
    if (!hasFocused) return;

    if (inputValue.trim().length < 3 || isOptionSelected) {
      setIsOpen(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;

    const fetchOptions = async () => {
      setLoading(true);

      try {
        const { options: fetchedOptionGroups } = await memoizedLoadOptions(
          inputValue,
          newAbortController.signal
        );

        if (newAbortController.signal.aborted) return;

        const sortedOptions = fetchedOptionGroups.sort(
          (a, b) => sortOrder.indexOf(a.category) - sortOrder.indexOf(b.category)
        );
        setCurrentOptions(sortedOptions);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch options:", error);
        }
      } finally {
        if (!newAbortController.signal.aborted) {
          setLoading(false);
        }

      }
    };

    fetchOptions();

    return () => {
      newAbortController.abort();
    };
  }, [inputValue, isOptionSelected, memoizedLoadOptions]);

  useEffect(() => {
    if (inputValue && inputValue.trim() === "") {
      setIsOpen(false);
    } else if (currentOptions.some((group) => group.options.length > 0)) {
      setIsOpen(hasFocused);
    } else {
      setIsOpen(false);
    }
  }, [inputValue, currentOptions, hasFocused]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setIsOptionSelected(false);
    if (newValue.trim() === '') {
      setIsOpen(false);
      onOptionSelect(null);
    }
  };

  const handleOptionClick = (option: Option) => {
    setInputValue(option.label);
    setIsOptionSelected(true);
    setIsOpen(false);
    setCurrentOptions([]);
    onOptionSelect(option);
    dispatch(
      setSearchValues({
        destination: {
          label: option.label,
          country: option.country,
          isProperty: option.isProperty,
          value: option.value,
          countryCode: option.countryCode,
          city: option.isProperty ? option.CITY_NAME : option.NAME_SMALL_CAPS,
          CITY_NAME: option.CITY_NAME,
          NAME_SMALL_CAPS: option.NAME_SMALL_CAPS
        }

      })
    );
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: any) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const sortOrder = ["city", "province_state", "airport", "point_of_interest", "property"];

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const router = useRouter();
  const dispatch = useDispatch();
  const searchState = useSelector((state: RootState) => state.hotelSearch);

  useEffect(() => {
    const fetchDestination = async () => {
      setLoadingLabel(true);
      try {
        if (searchState.destination.value.trim() !== "") {
          const res = await loadDestination([searchState.destination.value], router.locale ?? "en");

          if (res && res.options?.length > 0 && inputValue && inputValue.trim() !== "") {
            const { label, country, value, countryCode, isProperty , CITY_NAME  ,NAME_SMALL_CAPS } = res.options[0];
            setInputValue(label)
            dispatch(
              setSearchValues({
                destination: {
                  label: label,
                  country: country,
                  isProperty: isProperty,
                  value: value,
                  countryCode: countryCode,
                  city: isProperty ? CITY_NAME : NAME_SMALL_CAPS,
                  CITY_NAME:CITY_NAME,
                  NAME_SMALL_CAPS:NAME_SMALL_CAPS
                }
              })
            );
          }
        }
      } catch (error) {
        console.error("Failed to load destination", error);
      } finally {
        setLoadingLabel(false);
      }
    };

    fetchDestination();
  }, [router.locale]);

  const getIconForCategory = (category: string) => {
    switch (category) {
      case "city":
      case "province_state":
        return <MapPinLineIcon />;
      case "airport":
        return <AirplaneIcon />;
      case "property":
        return <HotelPropertyIcon />;
      case "point_of_interest":
        return <PointIcon />;
      default:
        return null;
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // if (inputValue) {
    event.target.select();
    // }
    setHasFocused(true);
    setIsOptionSelected(false);
    setIsOpen(false);
    setCurrentOptions([]);

  };

  const handleClear = () => {
    setInputValue("");
    setIsOptionSelected(false);
    setIsOpen(false);
    onOptionSelect(null);
    setHasFocused(false);
    setCurrentOptions([]);
  };

  const replaceUnderscores = (name: string) => name.replace(/_/g, ' ');
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focusAndSelect: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    },
    clearInput: () => {
      handleClear();
      inputRef.current?.focus();
      inputRef.current?.select();
    },
  }));


  return (
    <div className='w-full  flex flex-col self-stretch ' data-testid='AutoSuggestMobile'>
      <div className="relative px-4 ">
        {loadingLabel && inputValue !== "" ? <div className="flex !items-start !justify-start w-full"><Skeleton height={20} width={100} /></div> :
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
       
            onClick={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
              inputRef.current?.select();
              if (onClick) onClick(e);
            }}
            onFocus={(e) => {
              inputRef.current?.focus();
              inputRef.current?.select();
              handleFocus(e)
            }}
            className={`${styles.AutoSuggestInput} ${errorStyle ? styles.error : ""} w-full pe-10 border-none   focus-visible:ring-1  focus-visible:ring-secondary focus-visible:border-secondary  text-base flex items-center self-stretch bg-[#FFFFFF] text-start py-[14px]  px-4   rounded-[8px] border-0`}

          />}
        <div className="absolute top-1/2 ltr:right-8 rtl:left-8 transform -translate-y-1/2">
          <SearchIcon size={16} color="#9CA3AF" />
        </div>
      </div>

      <div className="self-stretch  h-[1px] my-4">
      </div>
      {
        loading && <Loader className="absolute top-[136px]" />

      }

      {currentOptions.map(
        (group) =>
          group.options.length > 0 && (
            <div key={group.category} className={` px-4 bg-[#F7FAFD]  `}>
              <div className={`text-[#1E293B] text-[14px] my-3 leading-6  font-medium capitalize`}>
                <>{replaceUnderscores(t(group.category))} </>
              </div>
              {group.options.map((option, index) => (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Select countries`}
                  key={index}
                  className={` flex px-6  justify-center items-center flex-col`}
                  onClick={() => handleOptionClick(option)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleOptionClick(option)
                    }
                  }}>
                  <div className="flex flex-row justify-between w-full  my-2  items-start">
                    <div className="flex flex-col items-start w-full ">
                      <div className="flex flex-col items-start ">
                        <span className="text-[#1E293B] text-[14px] leading-6 mt-[1px] flex gap-4 items-center" >

                          {group.category === "property" ? (
                            <img src={option.IMAGE_350 ? option.IMAGE_350 : "/images/hotel-placeholder.jpg"} className="w-[29px] h-[28px] rounded-full object-fill" />
                          ) : (
                            <span className="">
                              {getIconForCategory(group.category)}
                            </span>
                          )
                          }
                          {option.label}
                        </span>
                     
                      </div>
                      {index !== group.options.length - 1 && (
                        <div className="self-stretch bg-[#E2EDF7] h-[1px] my-[12px]">
                        </div>
                      )}


                    </div>

                  </div>
                </div>
              ))}
            </div>
          )
      )}



    </div >
  );
});



export default AutoSuggestMobile;
